<template>
  <div>
    <KTCard ref="preview" v-bind:title="'Edit'">
      <template v-slot:title>
        <h3 class="card-label">
          <router-link :to="{ name: 'list-service-invoices' }">{{
            $t("MENU.ITEM.SERVICE.INVOICES")
          }}</router-link>
          <i class="mr-2"></i>
          <small class="">{{ $t("LABELS.EDIT") }}</small>
        </h3>
      </template>
      <template v-slot:toolbar>
        <div class="example-tools justify-content-center">
          <BackButton></BackButton>
          <DeleteButton
            v-on:onDelete="onDelete()"
            v-permission="['service-delete']"
          ></DeleteButton>
          <SaveButtonDropdown
            :defaultAction="'continue'"
            :isLoading="isLoadingServiceInvoice"
            v-on:save="onSave($event)"
          ></SaveButtonDropdown>
        </div>
      </template>
      <template v-slot:body>
        <ServiceInvoicesForm
          ref="ServiceInvoicesForm"
          :form="form"
          @add-row="handleAddRow"
          @track-deleted-row="trackDeletedRow"
          @track-deleted-expense="trackDeletedExpense"
        ></ServiceInvoicesForm>

        <v-overlay :value="!firstLoader && isLoadingServiceInvoice">
          <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
      </template>
      <template v-slot:toolbarfoot>
        <div class="example-tools justify-content-center">
          <BackButton></BackButton>
          <DeleteButton
            v-on:onDelete="onDelete()"
            v-permission="['service-delete']"
          ></DeleteButton>
          <SaveButtonDropdown
            :defaultAction="'continue'"
            :isLoading="isLoadingServiceInvoice"
            v-on:save="onSave($event)"
          ></SaveButtonDropdown>
        </div>
      </template>
    </KTCard>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import {
  FETCH_SERVICE_INVOICE,
  UPDATE_SERVICE_INVOICE,
  DELETE_SERVICE_INVOICE
} from "@/modules/service-invoices/store/service-invoices.module";

import SaveButtonDropdown from "@/view/content/forms/components/SaveButtonDropdown.vue";
import KTCard from "@/view/content/Card.vue";
import ServiceInvoicesForm from "../components/ServiceInvoicesForm.vue";
import { mapGetters } from "vuex";

export default {
  name: "ServiceInvoicesEdit",
  components: {
    KTCard,
    SaveButtonDropdown,
    ServiceInvoicesForm
  },
  data() {
    return {
      errors: {},
      loading: true,
      firstLoader: true,
      deletedRowIds: [],
      deletedExpenseIds: [],
      form: {
        serviceVendorId: null,
        invoiceNumber: null,
        invoiceDate: null,
        qty: 1,
        totalNoVat: "0.00",
        totalWithVat: "0.00",
        vat: "0.00",
        toAccounting: true,
        rows: []
      }
    };
  },
  computed: {
    ...mapGetters(["isLoadingServiceInvoice"])
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      {
        title: this.$i18n.t("MENU.ITEM.SERVICE.INVOICES"),
        route: { name: "list-service-invoices" }
      },
      { title: this.$i18n.t("MENU.ITEM.EDIT") }
    ]);
    this.getServiceInvoice();
  },
  methods: {
    handleAddRow() {
      this.$refs.ServiceInvoicesForm.addNewRow();
    },
    getServiceInvoice() {
      let vm = this;
      let id = this.$route.params.id;
      this.id = id;
      this.$store
        .dispatch(FETCH_SERVICE_INVOICE, id)
        .then(data => {
          vm.firstLoader = false;
          vm.loading = false;

          const transformedData = {
            ...data.data,
            rows: data.data.rows
              ? data.data.rows.map(row => {
                  const expense =
                    row.serviceExpenses && row.serviceExpenses.length > 0
                      ? row.serviceExpenses[0]
                      : null;

                  return {
                    id: row.id,
                    name: row.name || "",
                    code: row.code || "",
                    price: row.price || 0,
                    totalQty: row.totalQty || 0,
                    totalPrice: row.totalPrice || 0,
                    machine: expense?.machine || null,
                    machineId: expense?.machineId || null,
                    serviceWarehouseItemId:
                      expense?.serviceWarehouseItemId || null,
                    tempId: null
                  };
                })
              : []
          };

          this.form = transformedData;
        })
        .catch(response => {
          if (response.status === 404) {
            vm.$notify({
              group: "notify",
              type: "error",
              text: "Not Found"
            });
            this.$router.push({ name: "list-service-invoices" });
          }
        });
    },
    trackDeletedRow(rowId) {
      if (rowId && !this.deletedRowIds.includes(rowId)) {
        this.deletedRowIds.push(rowId);
      }
    },
    trackDeletedExpense(expenseId) {
      if (expenseId && !this.deletedExpenseIds.includes(expenseId)) {
        this.deletedExpenseIds.push(expenseId);
      }
    },
    preparePayload(form) {
      const transformedRows = form.rows.map(row => {
        const serviceExpenses = [];

        const expense = {
          quantity: parseFloat(row.totalQty) || 0,
          machineId: null,
          serviceWarehouseItemId: null,
          staffId: null
        };

        if (row.machineId) {
          expense.machineId = row.machineId;

          if (row.machine && row.machine.id && row.machine.id.lastUsedStaffId) {
            expense.staffId = row.machine.id.lastUsedStaffId;
          }
        }

        if (row.serviceWarehouseItemId) {
          expense.serviceWarehouseItemId = row.serviceWarehouseItemId;
        }

        serviceExpenses.push(expense);

        return {
          id: row.id,
          name: row.name,
          code: row.code,
          price: parseFloat(row.price) || 0,
          totalQty: parseFloat(row.totalQty) || 0,
          totalPrice: parseFloat(row.totalPrice) || 0,
          serviceExpenses: serviceExpenses
        };
      });

      const payload = {
        serviceVendorId: form.serviceVendorId,
        invoiceNumber: form.invoiceNumber,
        invoiceDate: form.invoiceDate,
        qty: form.qty,
        totalNoVat: form.totalNoVat,
        totalWithVat: form.totalWithVat,
        vat: form.vat,
        toAccounting: form.toAccounting,
        rows: transformedRows
      };

      if (this.deletedRowIds.length > 0) {
        payload.deletedRowIds = this.deletedRowIds;
      }
      if (this.deletedExpenseIds.length > 0) {
        payload.deletedExpenseIds = this.deletedExpenseIds;
      }

      return payload;
    },
    onSave(next) {
      let vm = this;
      this.$refs.ServiceInvoicesForm.$v.form.$touch();
      if (this.$refs.ServiceInvoicesForm.$v.form.$anyError) {
        const errorMessages = [];

        if (this.$refs.ServiceInvoicesForm.$v.form.serviceVendorId.$error) {
          errorMessages.push(this.$t("FORM_LABELS.VENDOR"));
        }
        if (this.$refs.ServiceInvoicesForm.$v.form.invoiceNumber.$error) {
          errorMessages.push(this.$t("FORM_LABELS.INVOICE_NUMBER"));
        }
        if (this.$refs.ServiceInvoicesForm.$v.form.invoiceDate.$error) {
          errorMessages.push(this.$t("FORM_LABELS.DATE"));
        }

        const errorText =
          errorMessages.length > 0
            ? `${this.$t("ALERTS.FILL_FIELDS")}: ${errorMessages.join(", ")}`
            : this.$t("ALERTS.FILL_REQUIRED_FIELDS");

        vm.$notify({
          group: "notify",
          type: "error",
          title: "<i class='flaticon-close'></i> " + this.$t("ALERTS.WARNING"),
          text: errorText
        });
        return;
      }

      // Validate rows
      if (!this.$refs.ServiceInvoicesForm.validateRows()) {
        const errorText =
          this.$t("ALERTS.EXPENSE_REQUIRED") ||
          "Each service row must have at least one expense with valid quantity";
        vm.$notify({
          group: "notify",
          type: "error",
          title: "<i class='flaticon-close'></i> " + this.$t("ALERTS.WARNING"),
          text: errorText
        });
        return;
      }

      const payload = this.preparePayload(this.form);

      this.$store
        .dispatch(UPDATE_SERVICE_INVOICE, { id: this.id, payload: payload })
        .then(data => {
          vm.deletedRowIds = [];
          vm.deletedExpenseIds = [];

          vm.errors = {};
          vm.$notify({
            group: "notify",
            type: "success",
            title: "<i class='flaticon2-checkmark'></i> Success",
            text: data.message
          });

          if (next == "continue") {
            vm.getServiceInvoice();
          }
          if (next == "add-row") {
            vm.getServiceInvoice();
          }
          if (next == "new") {
            this.$router.push({ name: "add-service-invoices" });
          }
          if (next == "exit") {
            this.$router.push({ name: "list-service-invoices" });
          }
        })
        .catch(response => {
          let errorText = response.data.message;
          if (response.data && response.data.errors) {
            vm.errors = response.data.errors;
            const formattedErrors = this.$errors.format(response.data.errors);
            if (formattedErrors.length > 0) {
              errorText = formattedErrors.join(", ");
            }
          }

          vm.$notify({
            group: "notify",
            type: "error",
            title: this.$t("ALERTS.ERROR"),
            text: errorText
          });
        });
    },
    onDelete() {
      this.$dialog.confirm(this.$t("BUTTON.PLEASE_CONFIRM")).then(() => {
        this.$store
          .dispatch(DELETE_SERVICE_INVOICE, this.$route.params.id)
          .then(data => {
            this.$notify({
              group: "notify",
              type: "success",
              title: "<i class='flaticon2-checkmark'></i> Success",
              text: data.message
            });
            this.$router.push({ name: "list-service-invoices" });
          })
          .catch(response => {
            this.$notify({
              group: "notify",
              type: "error",
              title: this.$t("ALERTS.ERROR"),
              text: response.data.message
            });
          });
      });
    }
  }
};
</script>
